import React, { lazy, memo } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { BookingCard } from '../components/BookingCard';
import { useSeo } from '../lib/useSeo';
import { servicesData } from '../data/services';
import { ArrowRight } from 'lucide-react';
export function SoundInfusedRenewal() {
  const service = servicesData.find(s => s.id === 'sound-infused-renewal')!;
  const otherServices = servicesData.filter(s => s.id !== service.id);
  useSeo({
    title: service.title,
    description: service.summary,
    path: service.link,
    image: service.image
  });
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-560">
      <Header data-id="element-561" />

      {/* Hero Section */}
      <section className="relative h-[65vh] min-h-[480px] flex items-center justify-center overflow-hidden" data-id="element-562">
        <div className="absolute inset-0 z-0" data-id="element-563">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-564" />
          <div className="absolute inset-0 bg-charcoal/40" data-id="element-565"></div>
        </div>

        <div className="relative z-10 text-center px-6 mt-16" data-id="element-566">
          <h1 className="text-5xl md:text-7xl font-serif text-white mb-4 drop-shadow-md" data-id="element-567">
            {service.title}
          </h1>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-568">
          <WavyDivider fill="#F8F2E4" data-id="element-569" />
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-6" data-id="element-570">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-571">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6" data-id="element-572">
            <p className="text-xl font-light text-charcoal-50 leading-relaxed italic" data-id="element-573">
              <strong className="font-medium not-italic text-charcoal" data-id="element-574">
                Feel lighter, clearer, and at peace.
              </strong>
              <br data-id="element-575" />
              Stress, repressed emotions, and toxins create static energy that
              disrupts your natural inner rhythm. Through the gentle power of
              sound and the subtle vibrations of tuning forks, your body begins
              to "hear" where it's out of tune — gently realigning itself and
              returning to a state of deep harmony and flow.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-576">
              A subtle yet powerful method of sound harmonization that helps the
              body recognize and retune dissonant areas in its energetic,
              electrical, and physical systems.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-577">
              Our biofield — the aura — stores imprints of past experiences as
              energetic congestion and frequency distortions. These dense areas
              reflect emotional, mental, or physical tension. Tuning forks
              detect these blockages — like a needle on a vinyl record revealing
              scratches — through shifts in tone, vibration, or resistance. With
              patient attunement, the energy softens and gradually releases what
              no longer serves, restoring coherence.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-578">
              As the body is mostly water, sound penetrates deeply. Each cell
              carries a natural frequency, which can be disrupted by stress or
              toxins. Tuning helps the system remember its original flow. Energy
              is released as bio-photons — light particles — which are returned
              to the body to reinforce balance.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-579">
              This method supports healing on physical, energetic, and emotional
              levels, not by erasing memories, but by transforming our
              relationship with them. The body relaxes, the mind quiets, and
              energy flows. Wounds become strength.
            </p>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-580">
            <BookingCard price={service.price} priceDetail={service.priceDetail} duration="Full session with consultation" format="In-person (Studio)" data-id="element-581" />
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="bg-sage-50 py-20 px-6 relative mt-12" data-id="element-582">
        <div className="absolute top-0 left-0 right-0 -translate-y-full pointer-events-none" data-id="element-583">
          <WavyDivider fill="#E8EFE7" position="top" data-id="element-584" />
        </div>

        <div className="max-w-7xl mx-auto" data-id="element-585">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-12 text-center" data-id="element-586">
            You might also like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-587">
            {otherServices.map(s => <Link key={s.id} to={s.link} className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-md transition-all" data-id="element-588">
                <div className="aspect-[4/3] overflow-hidden" data-id="element-589">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-590" />
                </div>
                <div className="p-6" data-id="element-591">
                  <h3 className="font-serif text-xl text-charcoal mb-2" data-id="element-592">
                    {s.title}
                  </h3>
                  <span className="text-sage-700 text-sm flex items-center group-hover:text-sage-600 transition-colors" data-id="element-593">
                    Explore <ArrowRight size={14} className="ml-1" data-id="element-594" />
                  </span>
                </div>
              </Link>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-595" />
    </div>;
}